import { useEffect } from 'react';

/**
 * Export Modal Component
 * Shows export progress and download button
 */
export function ExportModal({
  isOpen,
  onClose,
  clipCount,
  isExporting,
  exportStatus,
  error,
  onDownload,
}) {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !isExporting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, isExporting, onClose]);

  if (!isOpen) return null;

  const getStatusIcon = () => {
    if (error) {
      return (
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center" aria-hidden="true">
          <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    }

    if (exportStatus?.status === 'completed') {
      return (
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center" aria-hidden="true">
          <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    }

    // Processing spinner
    return (
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-100 flex items-center justify-center" role="status" aria-label="Generating export">
        <svg className="w-8 h-8 text-purple-600 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  };

  const getStatusMessage = () => {
    if (error) {
      return (
        <>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Export Failed</h3>
          <p className="text-sm text-red-600">{error}</p>
        </>
      );
    }

    if (exportStatus?.status === 'completed') {
      return (
        <>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Export Ready!</h3>
          <p className="text-sm text-gray-600 mb-1">
            Your {clipCount} clip{clipCount !== 1 ? 's' : ''} have been exported.
          </p>
          <p className="text-xs text-gray-500">
            Download link expires in 1 hour and can only be used once.
          </p>
        </>
      );
    }

    if (exportStatus?.status === 'processing') {
      return (
        <>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating Export...</h3>
          <p className="text-sm text-gray-600">
            Creating HTML file with your {clipCount} clip{clipCount !== 1 ? 's' : ''}.
          </p>
        </>
      );
    }

    return (
      <>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Queuing Export...</h3>
        <p className="text-sm text-gray-600">
          Preparing to export {clipCount} clip{clipCount !== 1 ? 's' : ''}.
        </p>
      </>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-xs"
        onClick={() => !isExporting && onClose()}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 text-center"
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-modal-title"
      >
        {getStatusIcon()}
        
        <div id="export-modal-title">
          {getStatusMessage()}
        </div>

        <div className="mt-6 space-y-3">
          {exportStatus?.status === 'completed' && (
            <button
              onClick={onDownload}
              className="w-full px-4 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
              aria-label="Download HTML export file"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download HTML File
            </button>
          )}

          {(exportStatus?.status === 'completed' || error) && (
            <button
              onClick={onClose}
              className="w-full px-4 py-2 text-gray-600 font-medium hover:text-gray-900 transition-colors"
            >
              Close
            </button>
          )}

          {isExporting && !error && (
            <p className="text-xs text-gray-400">
              Please wait, this may take a few seconds...
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
